/* eslint-disable max-lines */
/* eslint-disable import/prefer-default-export */
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { isNull, map, reduce, replace, trim } from 'lodash';

import titleCase, { capitalizeName } from 'common/utils/titleCase';

import graphqlClient from '../graphql';
import {
  addGuarantorGql,
  addPatientAddressGql,
  addPatientGql,
  addPatientInsuranceGql,
  addPatientMedicalHistoryGql,
  addPatientMedicationGql,
  addPatientPharmacyGql,
  addPatientProviderGql,
  associatePatientWithExternalProfileGql,
  createLineItemNoteGql,
  createPatientCommentGql,
  createRefillOrderGql,
  deactivateInsuranceGql,
  deactivatePatientProviderGql,
  deleteGuarantorGql,
  deletePatientAddressGql,
  deletePatientCommentGql,
  deletePatientMedicationGql,
  deletePatientPharmacyGql,
  duplicatePatientGql,
  editOrderShipmentGql,
  editPatientGql,
  editPatientPrescriptionGql,
  getAuthorizationsGql,
  getCurrentTestClaimIdentityGql,
  getDownloadUrlForInvoiceKeyGql,
  getGuarantorsGql,
  getPatientAddressesGql,
  getPatientGql,
  getPatientInsurancesGql,
  getPatientMedicalHistoryGql,
  getPatientPharmaciesGql,
  getPatientPrescriptionDetailsGql,
  getPatientProvidersGql,
  getPatientUsersGql,
  getPatientsGql,
  getPrescriptionAttestationGql,
  getTransferOutDocumentGql,
  listFailedAdjudicationsGql,
  listLineItemNotesGql,
  listPatientCommentsGql,
  listPatientInvoicesGql,
  listPatientMedicationsGql,
  listPatientOrderShipmentsGql,
  listPatientOrdersGql,
  listPatientPrescriptionsGql,
  listPatientRequestsGql,
  noteActionGql,
  onboardPatientGql,
  patientHistoryTrackListGql,
  recordAuditEventGql,
  runTestClaimGql,
  saveTestClaimResultGql,
  searchQS1ProfileGql,
  setPartnerExternalIdGql,
  setPatientContactsForPartnersGql,
  triggerForgotPasswordGql,
  updateAddOnQuantityGql,
  updateGuarantorGql,
  updatePatientAddressGql,
  updatePatientAuthorizationsGql,
  updatePatientInsuranceGql,
  updatePatientMedicationGql,
  updatePatientPharmacyGql,
  updatePatientProviderGql,
  updateRefillAdjudicationStateGql,
  validateAddressGql,
  validatePrescriptionForRefillGql,
} from './gql';
import {
  transformFullPatientDetails,
  transformGetPatientActionItems,
  transformGetPatientUsers,
  transformGetPatients,
  transformGuarantorData,
  transformLineItemNotes,
  transformListFailedAdjudications,
  transformListPatientComments,
  transformNote,
  transformPatientAddress,
  transformPatientAddresses,
  transformPatientContactsFromPartners,
  transformPatientData,
} from './transform';

export const getPatientsApi = ({ page, sortBy, filter }) => {
  const transformedFilter = {
    ...filter,
    dob: filter.dob && format(new Date(filter.dob), 'yyyy-MM-dd'),
  };

  return graphqlClient
    .request(getPatientsGql, {
      offset: (page - 1) * 25,
      sortBy,
      filter: reduce(
        // omit keys if values are null
        transformedFilter,
        (result, value, key) => ({
          ...result,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(transformGetPatients);
};

export const addPatientApi = ({ patientData }) => {
  return graphqlClient.request(addPatientGql, { patientData }).then(({ addPatient }) => addPatient);
};

export const editPatientApi = ({ patientData }) => {
  return graphqlClient
    .request(editPatientGql, { patientData })
    .then(({ editPatient }) => editPatient);
};

export const listPatientCommentsApi = ({ mpi, offset, searchText }) => {
  return graphqlClient
    .request(listPatientCommentsGql, {
      mpi,
      offset,
      // BE has length validation, if search text is a blank string, therefore we need to set it to undefined
      filter: { searchText: searchText || undefined, type: 'INFO' },
    })
    .then(transformListPatientComments);
};

export const createPatientCommentApi = ({ mpi, message, contextId }) => {
  return graphqlClient
    .request(createPatientCommentGql, { mpi, message, contextId })
    .then(({ createPatientComment }) => createPatientComment);
};

export const deletePatientCommentApi = ({ patientCommentId }) => {
  return graphqlClient
    .request(deletePatientCommentGql, { patientCommentId })
    .then(({ deletePatientComment }) => deletePatientComment);
};

export const getPatientApi = ({ mpi }) => {
  return graphqlClient
    .request(getPatientGql, { mpi })
    .then(({ getPatient }) => transformPatientData({ getPatient }));
};

export const addGuarantorApi = ({ guarantor }) => {
  return graphqlClient
    .request(addGuarantorGql, { guarantor })
    .then(({ addGuarantor }) => addGuarantor);
};

export const getGuarantorsApi = ({ mpi }) => {
  return graphqlClient.request(getGuarantorsGql, { mpi }).then(transformGuarantorData);
};

export const updateGuarantorApi = ({ guarantor }) => {
  return graphqlClient
    .request(updateGuarantorGql, { guarantor })
    .then(({ updateGuarantor }) => updateGuarantor);
};

export const deleteGuarantorApi = ({ mpi, guarantorId }) => {
  return graphqlClient.request(deleteGuarantorGql, { mpi, guarantorId });
};

export const addPatientMedicationApi = ({ medicationData }) => {
  return graphqlClient
    .request(addPatientMedicationGql, { medicationData })
    .then(({ addPatientMedication }) => addPatientMedication);
};

export const listPatientMedicationsApi = ({ mpi }) => {
  return graphqlClient
    .request(listPatientMedicationsGql, { mpi })
    .then(({ listPatientMedications }) => listPatientMedications.results);
};

export const updatePatientMedicationApi = ({ medication }) => {
  return graphqlClient
    .request(updatePatientMedicationGql, { medication })
    .then(({ updatePatientMedication }) => updatePatientMedication);
};

export const deletePatientMedicationApi = ({ mpi, patientMedicationId }) => {
  return graphqlClient.request(deletePatientMedicationGql, { mpi, patientMedicationId });
};

export const getPatientMedicalHistoryApi = ({ mpi }) => {
  return graphqlClient
    .request(getPatientMedicalHistoryGql, { mpi })
    .then(({ getPatientMedicalHistory }) => getPatientMedicalHistory);
};

export const addPatientMedicalHistoryApi = ({ newMedicalHistory }) => {
  return graphqlClient
    .request(addPatientMedicalHistoryGql, { input: newMedicalHistory })
    .then(({ addPatientMedicalHistory }) => addPatientMedicalHistory);
};

export const getAuthorizationsApi = ({ mpi }) => {
  return graphqlClient
    .request(getAuthorizationsGql, { mpi })
    .then(({ getAuthorizations }) => getAuthorizations);
};

export const updatePatientAuthorizationsApi = ({ mpi, authorization }) => {
  return graphqlClient
    .request(updatePatientAuthorizationsGql, { mpi, authorization })
    .then(({ updatePatientAuthorizations }) => updatePatientAuthorizations);
};

export const addPatientInsuranceApi = ({ insurance }) => {
  return graphqlClient
    .request(addPatientInsuranceGql, { insurance })
    .then(({ addPatientInsurance }) => addPatientInsurance);
};

export const getPatientInsurancesApi = ({ mpi }) => {
  return graphqlClient
    .request(getPatientInsurancesGql, { mpi })
    .then(({ getPatientInsurances }) => getPatientInsurances);
};

export const updatePatientInsuranceApi = ({ insurance }) => {
  return graphqlClient
    .request(updatePatientInsuranceGql, { insurance })
    .then(({ updatePatientInsurance }) => updatePatientInsurance);
};

export const deactivateInsuranceApi = ({ patientInsuranceId }) => {
  return graphqlClient.request(deactivateInsuranceGql, { patientInsuranceId });
};

export const onboardPatientApi = ({ onboardPatientInput }) => {
  return graphqlClient
    .request(onboardPatientGql, { onboardPatientInput })
    .then(({ onboardPatient }) => ({
      patient: onboardPatient.patient.patientDemographics,
      authorization: onboardPatient.authorization.authorization,
    }));
};

export const getFullPatientDetailsApi = ({ mpi }) => {
  return graphqlClient
    .batchRequests([
      { document: getGuarantorsGql, variables: { mpi } },
      { document: getPatientInsurancesGql, variables: { mpi } },
      { document: listPatientMedicationsGql, variables: { mpi } },
      { document: getAuthorizationsGql, variables: { mpi } },
    ])
    .then(transformFullPatientDetails);
};

export const addPatientProviderApi = ({ provider }) => {
  return graphqlClient
    .request(addPatientProviderGql, { provider })
    .then(({ addPatientProvider }) => addPatientProvider);
};

export const updatePatientProviderApi = ({ provider }) => {
  return graphqlClient
    .request(updatePatientProviderGql, { provider })
    .then(({ updatePatientProvider }) => updatePatientProvider);
};

export const deactivatePatientProviderApi = ({ patientProviderId }) => {
  return graphqlClient.request(deactivatePatientProviderGql, { patientProviderId });
};

export const getPatientProvidersApi = ({ mpi }) => {
  return graphqlClient
    .request(getPatientProvidersGql, { mpi })
    .then(({ getPatientProviders }) => getPatientProviders);
};

export const runTestClaimApi = ({ mpi, patientInsuranceId }) => {
  return graphqlClient
    .request(runTestClaimGql, { input: { mpi, patientInsuranceId } })
    .then(({ runTestClaim }) => runTestClaim);
};

export const saveTestClaimResultApi = ({ saveTestClaimResultInput }) => {
  return graphqlClient
    .request(saveTestClaimResultGql, { input: saveTestClaimResultInput })
    .then(({ saveTestClaimResult }) => saveTestClaimResult);
};

export const getCurrentTestClaimIdentityApi = ({ mpi }) => {
  return graphqlClient
    .request(getCurrentTestClaimIdentityGql, { mpi })
    .then(({ getCurrentTestClaimIdentity }) => getCurrentTestClaimIdentity);
};

export const addPatientPharmacyApi = ({ pharmacy }) => {
  return graphqlClient
    .request(addPatientPharmacyGql, { pharmacy })
    .then(({ addPatientPharmacy }) => addPatientPharmacy);
};

export const getPatientPharmaciesApi = ({ mpi }) => {
  return graphqlClient
    .request(getPatientPharmaciesGql, { mpi })
    .then(({ getPatientPharmacies }) => getPatientPharmacies);
};

export const updatePatientPharmacyApi = ({ pharmacy }) => {
  return graphqlClient
    .request(updatePatientPharmacyGql, { pharmacy })
    .then(({ updatePatientPharmacy }) => updatePatientPharmacy);
};

export const deletePatientPharmacyApi = ({ patientPharmacyId }) => {
  return graphqlClient.request(deletePatientPharmacyGql, { patientPharmacyId });
};

export const setPartnerExternalIdApi = ({ mpi, partnerId, identityValue }) => {
  return graphqlClient
    .request(setPartnerExternalIdGql, { mpi, partnerId, identityValue })
    .then(({ setPartnerExternalId }) => setPartnerExternalId);
};

// Each partner goes with its full desired contact: a cleared field goes as null, which removes it.
// The phone goes as bare digits, the shape partner phones are stored in. All-or-nothing on the server.
export const setPatientContactsForPartnersApi = ({ mpi, partnerContacts }) => {
  return graphqlClient
    .request(setPatientContactsForPartnersGql, {
      input: {
        patientId: mpi,
        contacts: map(partnerContacts, ({ partnerId, email, phoneNumber }) => ({
          partnerUUID: partnerId,
          patientEmail: trim(email) || null,
          patientPhoneNumber: replace(phoneNumber, /\D/g, '') || null,
        })),
      },
    })
    .then(({ setPatientContactsForPartners }) =>
      transformPatientContactsFromPartners(setPatientContactsForPartners)
    );
};

export const getPatientActionItemsApi = ({ mpi }) => {
  return graphqlClient
    .batchRequests([
      { document: listPatientRequestsGql, variables: { mpi } },
      { document: listPatientOrdersGql, variables: { mpi } },
    ])
    .then(transformGetPatientActionItems);
};

export const listFailedAdjudicationsApi = ({ page, sortBy, filter }) => {
  const defaultItemsPerPage = Number(process.env.REACT_APP_DEFAULT_ITEMS_PER_PAGE);
  const defaultPageOffset = Number(process.env.REACT_APP_DEFAULT_PAGE_OFFSET);

  return graphqlClient
    .request(listFailedAdjudicationsGql, {
      limit: defaultItemsPerPage,
      offSet: (page - 1) * defaultItemsPerPage || defaultPageOffset,
      sortBy,
      filter: reduce(
        filter,
        (result, value, key) => ({
          ...result,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(transformListFailedAdjudications);
};

export const updateRefillAdjudicationStateApi = ({ rxRequestId, state }) =>
  graphqlClient.request(updateRefillAdjudicationStateGql, { rxRequestId, state });

// Title-cases the drug-name/strength fields on a prescription before it reaches Redux
// (listPatientPrescriptionsApi/getPatientPrescriptionDetailsApi feed patientActions.loadPrescriptions
// / loadPrescriptionDetails directly). The two GraphQL responses don't share a shape -- the
// top-level field is `drug`/`strength` (not `drugName`), and only the list response carries
// `prescriptionOverride.productDetails`/`drugProfile` -- so each nested piece is optional-chained
// and guarded to be a no-op when absent, letting this same helper serve both endpoints.
const titleCaseNestedDrugProfile = (drugProfile) =>
  drugProfile
    ? {
        ...drugProfile,
        ...(drugProfile.drugName ? { drugName: titleCase(drugProfile.drugName) } : {}),
        ...(drugProfile.strength ? { strength: titleCase(drugProfile.strength) } : {}),
      }
    : drugProfile;

const titleCasePrescription = (prescription) => ({
  ...prescription,
  ...(prescription.drug ? { drug: titleCase(prescription.drug) } : {}),
  ...(prescription.strength ? { strength: titleCase(prescription.strength) } : {}),
  ...(prescription.prescribedDrugName
    ? { prescribedDrugName: titleCase(prescription.prescribedDrugName) }
    : {}),
  ...(prescription.practice
    ? {
        practice: {
          ...prescription.practice,
          practiceName: capitalizeName(prescription.practice.practiceName),
        },
      }
    : {}),
  ...(prescription.prescriptionOverride
    ? {
        prescriptionOverride: {
          ...prescription.prescriptionOverride,
          ...(prescription.prescriptionOverride.productDetails
            ? {
                productDetails: titleCaseNestedDrugProfile(
                  prescription.prescriptionOverride.productDetails
                ),
              }
            : {}),
          ...(prescription.prescriptionOverride.drugProfile
            ? {
                drugProfile: titleCaseNestedDrugProfile(
                  prescription.prescriptionOverride.drugProfile
                ),
              }
            : {}),
        },
      }
    : {}),
});

export const listPatientPrescriptionsApi = ({ mpi, forceRefresh, offset, limit = 0 }) => {
  return graphqlClient
    .request(listPatientPrescriptionsGql, {
      mpi,
      forceRefresh,
      offset,
      limit,
    })
    .then(({ listPatientPrescriptions }) => ({
      count: listPatientPrescriptions.count,
      prescriptions: (listPatientPrescriptions.results ?? []).map(titleCasePrescription),
    }));
};

export const getPatientPrescriptionDetailsApi = ({
  mpi,
  prescriptionUUID,
  forceRefresh = false,
  offset,
  limit,
  prescriptionFilter,
}) => {
  return graphqlClient
    .request(getPatientPrescriptionDetailsGql, {
      mpi,
      prescriptionUUID,
      forceRefresh,
      offset,
      limit,
      prescriptionFilter,
    })
    .then(({ getPatientPrescriptionDetails }) => ({
      count: getPatientPrescriptionDetails.count,
      prescriptions: (getPatientPrescriptionDetails.results ?? []).map(titleCasePrescription),
      cacheRefreshed: getPatientPrescriptionDetails.cacheRefreshed,
    }));
};

export const getPatientAddressesApi = ({ mpi }) => {
  return graphqlClient
    .request(getPatientAddressesGql, { mpi })
    .then(({ getPatientAddresses }) =>
      transformPatientAddresses({ addresses: getPatientAddresses })
    );
};

export const addPatientAddressApi = ({ mpi, address }) => {
  return graphqlClient
    .request(addPatientAddressGql, { input: { mpi, ...address } })
    .then(({ addPatientAddress }) => {
      // eslint-disable-next-line no-underscore-dangle
      if (addPatientAddress?.__typename === 'Addresses') {
        return addPatientAddress;
      }

      return transformPatientAddress(addPatientAddress);
    });
};

export const deletePatientAddressApi = ({ mpi, patientAddressId }) => {
  return graphqlClient.request(deletePatientAddressGql, { mpi, patientAddressId });
};

export const updatePatientAddressApi = ({ mpi, address }) => {
  return graphqlClient
    .request(updatePatientAddressGql, { input: { mpi, ...address } })
    .then(({ updatePatientAddress }) => transformPatientAddress(updatePatientAddress));
};

export const listPatientInvoicesApi = ({ mpi, page }) => {
  return graphqlClient
    .request(listPatientInvoicesGql, { mpi, offset: (page - 1) * 10 })
    .then(({ listPatientInvoices }) => listPatientInvoices);
};

export const getDownloadUrlForInvoiceKeyApi = ({
  invoiceFileKey,
  invoiceId,
  mpi,
  invoiceServiceCustomerId,
}) => {
  return graphqlClient
    .request(getDownloadUrlForInvoiceKeyGql, {
      invoiceFileKey,
      invoiceId,
      mpi,
      invoiceServiceCustomerId,
    })
    .then(({ getDownloadUrlForInvoiceKey }) => getDownloadUrlForInvoiceKey);
};

export const getTransferOutDocumentApi = ({ lineItemId }) => {
  return graphqlClient
    .request(getTransferOutDocumentGql, { lineItemId })
    .then(({ getTransferOutDocument }) => getTransferOutDocument);
};

export const createLineItemNoteApi = ({ mpi, content, commentContext, isShippingRelated }) => {
  return graphqlClient
    .request(createLineItemNoteGql, { mpi, content, commentContext, isShippingRelated })
    .then(({ createPatientNote }) => transformNote(createPatientNote));
};

export const listLineItemNotesApi = ({ mpi, contextType, filter }) => {
  const limit = Number(process.env.REACT_APP_DEFAULT_ITEMS_PER_PAGE);
  const offset = Number(process.env.REACT_APP_DEFAULT_PAGE_OFFSET);
  return graphqlClient
    .request(listLineItemNotesGql, { mpi, contextType, filter, offset, limit })
    .then(({ listPatientNotes: listLineItemNotes }) => transformLineItemNotes(listLineItemNotes));
};

export const noteActionApi = ({ noteId, action }) => {
  return graphqlClient
    .request(noteActionGql, { action, noteId })
    .then(({ noteAction }) => noteAction);
};

export const listPatientOrderShipmentsApi = ({ mpi, filter }) => {
  return graphqlClient
    .request(listPatientOrderShipmentsGql, {
      mpi,
      filter: {
        ...filter,
        shipmentDate: filter?.shipmentDate
          ? format(utcToZonedTime(new Date(filter?.shipmentDate), 'utc'), 'yyyy-MM-dd')
          : undefined,
      },
    })
    .then(({ listPatientOrderShipments }) => listPatientOrderShipments);
};

export const getPatientUsersApi = ({ mpi }) => {
  return graphqlClient.request(getPatientUsersGql, { mpi }).then(transformGetPatientUsers);
};

export const resetPasswordApi = ({ userId }) => {
  return graphqlClient
    .request(triggerForgotPasswordGql, { peUserId: userId })
    .then(({ triggerPasswordResetForPEUser }) => triggerPasswordResetForPEUser);
};

export const listPatientOrdersApi = ({ mpi, filter, includeAllFacilities }) => {
  return graphqlClient
    .request(listPatientOrdersGql, { mpi, filter, includeAllFacilities })
    .then(({ listPatientOrders }) =>
      listPatientOrders.results.map((order) => ({
        ...order,
        partner: order.partner && {
          ...order.partner,
          partnerName: capitalizeName(order.partner.partnerName),
        },
        practice: order.practice && {
          ...order.practice,
          practiceName: capitalizeName(order.practice.practiceName),
        },
        lineItems: (order.lineItems || []).map((item) => ({
          ...item,
          itemName: item.itemName ? titleCase(item.itemName) : item.itemName,
          overriddenItemName: item.overriddenItemName
            ? titleCase(item.overriddenItemName)
            : item.overriddenItemName,
          strength: item.strength ? titleCase(item.strength) : item.strength,
          overriddenStrength: item.overriddenStrength
            ? titleCase(item.overriddenStrength)
            : item.overriddenStrength,
          originalItemName: item.originalItemName
            ? titleCase(item.originalItemName)
            : item.originalItemName,
          form: item.form ? titleCase(item.form) : item.form,
        })),
      }))
    );
};

export const editPatientPrescriptionApi = ({ prescriptionId, action, autoFill }) =>
  graphqlClient
    .request(editPatientPrescriptionGql, { action, prescriptionId, autoFill })
    .then(({ editPatientPrescription }) => editPatientPrescription);

export const searchQs1ProfileApi = ({ firstName, lastName, dob }) => {
  return graphqlClient
    .request(searchQS1ProfileGql, { firstName, lastName, dob })
    .then(({ searchQs1Profile }) => searchQs1Profile);
};

export const associatePatientWithExternalProfileApi = ({ mpi, externalProfile }) => {
  return graphqlClient
    .request(associatePatientWithExternalProfileGql, { mpi, externalProfile })
    .then(({ associatePatientWithExternalProfile }) => associatePatientWithExternalProfile);
};

export const editOrderShipmentApi = (input) => {
  return graphqlClient
    .request(editOrderShipmentGql, { ...input })
    .then(({ editOrderShipment }) => editOrderShipment);
};

export const validatePrescriptionForRefillApi = ({ prescriptionUUID }) =>
  graphqlClient
    .request(validatePrescriptionForRefillGql, prescriptionUUID)
    .then(({ validatePrescriptionForRefill }) => validatePrescriptionForRefill);

export const createRefillOrderApi = ({
  rxNumber,
  patientId,
  shippingAddress,
  deliveryInstructions,
}) =>
  graphqlClient
    .request(createRefillOrderGql, {
      input: {
        rxNumber,
        patientId,
        shippingAddress,
        ...(deliveryInstructions && { deliveryInstructions }),
      },
    })
    .then(({ createRefillOrder }) => createRefillOrder);

export const validateAddressApi = async ({ input }) =>
  graphqlClient
    .request(validateAddressGql, { input })
    .then(({ validateAddress }) => validateAddress);

export const getPatientOrderHistoryApi = ({ mpi, status, includeAllFacilities }) =>
  graphqlClient
    .request(listPatientOrdersGql, {
      mpi,
      filter: status ? { orderStatus: status } : {},
      includeAllFacilities,
    })
    .then(({ listPatientOrders }) => listPatientOrders.results);

export const getDuplicatePatientsApi = ({ patientId }) => {
  return graphqlClient
    .request(duplicatePatientGql, { patientId })
    .then(({ getDuplicatePatients }) => getDuplicatePatients);
};

export const recordAuditEventApi = ({ eventType, patientId, actionType, shipmentId }) => {
  return graphqlClient
    .request(recordAuditEventGql, { input: { eventType, patientId, actionType, shipmentId } })
    .then(({ recordAuditEvent }) => recordAuditEvent);
};

export const patientHistoryTrackListApi = ({ mpi, page = 1, limit = 25 }) => {
  return graphqlClient
    .request(patientHistoryTrackListGql, {
      filter: { patientId: mpi },
      limit,
      offset: (page - 1) * limit,
    })
    .then(({ patientHistoryTrackList }) => patientHistoryTrackList);
};

export const getPrescriptionAttestationApi = ({ attestationId }) => {
  return graphqlClient
    .request(getPrescriptionAttestationGql, { attestationId })
    .then(({ getPrescriptionAttestation }) => getPrescriptionAttestation);
};

export const updateAddOnQuantityApi = ({ lineItemId, orderId, quantity }) => {
  return graphqlClient
    .request(updateAddOnQuantityGql, { input: { lineItemId, orderId, quantity } })
    .then(({ updateAddOnQuantity }) => updateAddOnQuantity);
};
